import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Bell } from "lucide-react";
import NotificationSkeleton from "../components/NotificationSkeleton";

const POLL_INTERVAL = 30000;
const PAGE_SIZE = 10;

const getIcon = (type) => {
  switch (type) {
    case "comment": return "💬";
    case "vote": return "🗳️";
    case "report": return "🚩";
    default: return "🔔";
  }
};

const Notifications = () => {
  const { token } = useAuth();
  const { liveNotification, resetLiveNotification, setUnreadCount } = useWebSocket();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const pollingRef = useRef(null);

  const getTimeAgo = (timestamp) => {
    const diff = Math.floor((Date.now() - new Date(timestamp)) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const fetchNotifications = async (loadMore = false) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications`,
        {
          params: { limit: PAGE_SIZE, before: loadMore ? cursor : null },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const newNotifs = res.data.notifications || [];
      setNotifications(prev => loadMore ? [...prev, ...newNotifs] : newNotifs);
      setCursor(res.data.next_cursor || null);
      setHasMore(newNotifs.length === PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load notifications", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    setUnreadCount(0);
    axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/api/notifications/read-all`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {});
  }, [token, setUnreadCount]);

  useEffect(() => {
    if (!liveNotification) return;
    setNotifications(prev => {
      if (prev.some(n => n.id === liveNotification.id)) return prev;
      return [{ ...liveNotification, read: true }, ...prev];
    });
    resetLiveNotification();
  }, [liveNotification, resetLiveNotification]);

  useEffect(() => {
    if (!token) return;
    const startPolling = () => {
      if (!pollingRef.current)
        pollingRef.current = setInterval(() => fetchNotifications(false), POLL_INTERVAL);
    };
    const stopPolling = () => {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") { fetchNotifications(false); startPolling(); }
      else stopPolling();
    };
    fetchNotifications(false);
    if (document.visibilityState === "visible") startPolling();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => { stopPolling(); document.removeEventListener("visibilitychange", handleVisibility); };
  }, [token]);

  const handleClick = (notif) => {
    if (!notif?.data?.post_id) return;
    navigate(`/post/${notif.data.post_id}`);
    axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notif.id}/read`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
  };

  const groupByDate = (items) => {
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    items.forEach(notif => {
      const d = new Date(notif.created_at);
      const label = d.toDateString() === today ? "Today"
        : d.toDateString() === yesterday ? "Yesterday"
        : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
      if (!groups[label]) groups[label] = [];
      groups[label].push(notif);
    });
    return groups;
  };

  const grouped = groupByDate(notifications);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Notifications</h1>
            <p className="text-gray-500 dark:text-slate-400 mt-1">Things people did that involve you</p>
          </div>

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <NotificationSkeleton key={i} />)}
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-20">
              <Bell className="mx-auto w-10 h-10 text-gray-300 dark:text-slate-600 mb-3" />
              <p className="text-gray-500 dark:text-slate-400">No notifications yet</p>
            </div>
          ) : (
            Object.entries(grouped).map(([label, notifs]) => (
              <div key={label} className="mb-6">
                <p className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2 px-1">
                  {label}
                </p>
                <div className="space-y-1.5">
                  {notifs.map(notif => (
                    <div
                      key={notif.id}
                      onClick={() => handleClick(notif)}
                      className={`cursor-pointer flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-150
                        ${notif.read
                          ? "bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700 hover:border-cyan-300 dark:hover:border-cyan-600"
                          : "bg-cyan-50 dark:bg-slate-700 border-cyan-200 dark:border-cyan-600"
                        }`}
                    >
                      {/* Icon */}
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-base
                        ${notif.read ? "bg-gray-100 dark:bg-slate-700" : "bg-cyan-100 dark:bg-cyan-900/40"}`}>
                        {getIcon(notif.type)}
                      </div>

                      {/* Message */}
                      <p className={`flex-1 text-sm leading-snug
                        ${notif.read ? "text-gray-700 dark:text-slate-300" : "text-gray-900 dark:text-white font-medium"}`}>
                        {notif.message}
                      </p>

                      {/* Time — right side */}
                      <span className="text-xs text-gray-400 dark:text-slate-500 flex-shrink-0 ml-2">
                        {getTimeAgo(notif.created_at)}
                      </span>

                      {/* Unread dot */}
                      {!notif.read && (
                        <div className="w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}

          {hasMore && !loading && (
            <div className="text-center mt-6">
              <button
                onClick={() => fetchNotifications(true)}
                className="text-sm text-cyan-600 dark:text-cyan-400 hover:underline"
              >
                Load older notifications
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;